import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : render notifications
// =============================================================================
// ---Third-party imports
import React, { Component, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Message, Transition } from 'semantic-ui-react';
import clsx from 'clsx';
import { isString } from 'ldash';
// ---Framework imports
import { Portal, VIcon } from 'uix/components';
import log from 'uix/logging';
import { ucFirst } from 'uix/utils';
// ---Local imports
import S from './notify.scss';
import { dismissNotification } from './actions';
// ---Globals
const Notification = ({ message, header, type, color, details, position = 'top', id, icon, onCloseClick, visible })=>{
    let [cVisible, setCVisible] = useState(false);
    useEffect(()=>{
        // needed for missing entry transition
        setCVisible(visible);
    }, [
        visible
    ]);
    let headerText = header;
    let messageText = message;
    // better styling for positive cases
    let colorMapping = {
        SUCCESS: 'green',
        POSITIVE: 'green'
    };
    let cColor = !color && colorMapping[type] || undefined;
    // Set position of toast
    let verticalStyle = position.includes('top') ? {
        top: 0
    } : {
        bottom: 0
    };
    let horizontalStyle = position.includes('left') ? {
        justifyContent: 'flex-start'
    } : position.includes('right') ? {
        justifyContent: 'flex-end'
    } : {};
    // Set animation based on position
    let animation = position.includes('top') ? 'fade down' : 'fade up';
    animation = position.includes('left') ? 'fade right' : position.includes('right') ? 'fade left' : animation;
    if (!headerText) {
        headerText = message;
        messageText = null;
    }
    // Turn the type string into a boolean prop with the same name
    const typeObj = !color && {
        [type.toLowerCase()]: true
    };
    if (messageText) {
        const messagePieces = messageText.split('\n');
        messageText = messagePieces.map((piece, i)=>/*#__PURE__*/ _jsx("div", {
                children: piece
            }, i));
    } else if (!headerText) {
        headerText = ucFirst(type.toLowerCase());
    }
    return /*#__PURE__*/ _jsx("div", {
        className: S.toaster,
        style: {
            ...verticalStyle,
            ...horizontalStyle
        },
        children: /*#__PURE__*/ _jsx(Transition, {
            visible: cVisible,
            animation: animation,
            duration: 700,
            children: /*#__PURE__*/ _jsxs(Message, {
                floating: true,
                className: clsx(S.toast, isString(icon) && S.icon, onCloseClick && S.close),
                size: "tiny",
                color: cColor,
                icon: isString(icon),
                onDismiss: ()=>onCloseClick && onCloseClick(id),
                ...typeObj,
                children: [
                    icon && /*#__PURE__*/ _jsx(VIcon, {
                        name: icon
                    }),
                    /*#__PURE__*/ _jsxs(Message.Content, {
                        children: [
                            headerText && /*#__PURE__*/ _jsx(Message.Header, {
                                children: headerText
                            }),
                            messageText,
                            details ? /*#__PURE__*/ _jsx("pre", {
                                style: {
                                    fontSize: '0.7em',
                                    lineHeight: '1.0em'
                                },
                                children: details
                            }) : null
                        ]
                    })
                ]
            })
        })
    });
};
const actions = {
    dismissNotification
};
const mapState = (root)=>({
        notifications: root.data.notify
    });
export class NotificationManager extends Component {
    render() {
        let { notifications, dismissNotification } = this.props;
        let renderedNotifications = notifications.map((n)=>/*#__PURE__*/ _jsx(Notification, {
                onCloseClick: dismissNotification,
                ...n
            }, n.id));
        return /*#__PURE__*/ _jsx(Portal, {
            children: renderedNotifications
        });
    }
}
export default connect(mapState, actions)(NotificationManager);
